import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { IMAGES } from "@/data/content";

const EASE = [0.22, 1, 0.36, 1];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.14, delayChildren: 0.2 } },
};

const item = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: EASE } },
};

export const Hero = () => {
  const scrollTo = (id) => document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="top" className="relative min-h-screen flex items-center overflow-hidden pt-grain">
      <div className="absolute inset-0 z-0">
        <img
          src={IMAGES.heroInterior}
          alt="Spotless luxurious residential interior cleaned by Perfect Touch"
          className="w-full h-full object-cover object-center"
          loading="eager"
          fetchPriority="high"
          decoding="async"
        />
        <div className="absolute inset-0 bg-black/55" aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/50 to-black/10" aria-hidden="true" />
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#050505] to-transparent" aria-hidden="true" />
      </div>

      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="relative z-10 mx-auto max-w-[1400px] w-full px-6 md:px-20 py-24 sm:py-28"
      >
        <motion.div variants={item} className="flex items-center gap-3 mb-7">
          <span className="h-px w-10 bg-gold" aria-hidden="true" />
          <span className="overline whitespace-nowrap">
            <span className="sm:hidden">Premium Cleaning &amp; Logistics</span>
            <span className="hidden sm:inline">Premium Cleaning &amp; Logistics · NYC</span>
          </span>
        </motion.div>

        <motion.h1
          variants={item}
          className="font-display font-light text-white tracking-tight text-[2.1rem] leading-[1.1] sm:text-6xl lg:text-7xl max-w-3xl"
        >
          <span className="block">Detailed Cleaning.</span>
          <span className="block">Reliable Service.</span>
          <span className="block gold-gradient italic">The Perfect Touch.</span>
        </motion.h1>

        <motion.p
          variants={item}
          className="mt-7 max-w-xl text-base md:text-lg text-zinc-300 leading-relaxed"
        >
          Professional residential cleaning, selected commercial cleaning, Airbnb turnover cleaning,
          and on-demand logistics services across Queens, Brooklyn, Manhattan, and selected Long
          Island and Nassau County areas.
        </motion.p>

        <motion.div variants={item} className="mt-10 flex flex-col sm:flex-row gap-4">
          <button
            data-testid="hero-quote-btn"
            onClick={() => scrollTo("#quote")}
            className="group rounded-full bg-gold text-black font-semibold px-8 py-4 hover:bg-[#c5a059] transition-colors duration-300 flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-gold/60"
          >
            Request a Free Quote
            <ArrowDown size={18} className="group-hover:translate-y-1 transition-transform duration-300" />
          </button>
          <button
            data-testid="hero-services-btn"
            onClick={() => scrollTo("#residential")}
            className="rounded-full border border-white/40 text-white font-medium px-8 py-4 hover:border-gold hover:text-gold transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-gold/60"
          >
            Explore Our Services
          </button>
        </motion.div>
      </motion.div>
    </section>
  );
};
